import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { startWith } from 'rxjs';
import {
  FilterDropdownComponent,
  FilterDropdownOption,
} from '../../components/filter-dropdown/filter-dropdown.component';
import { ResetFilterButtonComponent } from '../../components/reset-filter-button/reset-filter-button.component';
import { TagFilterComponent } from '../../components/tag-filter/tag-filter.component';
import { countTagOptions, tagMatch } from '../../components/tag-filter/tag-filter.utils';
import {
  TEST_PLAN_STATUSES,
  TEST_PLAN_TYPES,
  TestPlan,
  TestPlanStatus,
  TestPlanType,
} from '../../models/test-plan.model';
import { TestPlanService } from '../../services/test-plan.service';
import { SavedViewService } from '../../services/saved-view.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { SavedView } from '../../models/saved-view.model';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { ProgressBarComponent } from '../../components/progress-bar/progress-bar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import {
  archiveErrorKey,
  buildTestPlanSavedViewFiltersJson,
  isOverdue,
  localIsoDate,
  parseTestPlanSavedViewFilters,
  realRunQueryParams,
  RunSummary,
  runSummary,
  testPlanType,
  writeErrorKey,
} from './test-plans.utils';
import { PlanStatusPillComponent } from './plan-status-pill/plan-status-pill.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { ListFocusScrollDirective } from '../../shared/scroll-container/list-focus-scroll.directive';
import { SaveToastService } from '../../components/save-toast/save-toast.service';

type SortKey = 'publicId' | 'title' | 'release' | 'status' | 'assignee' | 'progress' | 'dueDate';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_ROUTE_KEY = 'test-plans';
const STATUS_RANK: Record<TestPlanStatus, number> = { ENTWURF: 0, AKTIV: 1, ABGESCHLOSSEN: 2 };

@Component({
  selector: 'app-test-plans',
  imports: [
    ListFocusScrollDirective,
    CommonModule,
    TranslatePipe,
    DatePipe,
    MatIcon,
    SearchBarComponent,
    FilterDropdownComponent,
    ResetFilterButtonComponent,
    TagFilterComponent,
    StatGroupComponent,
    ConfirmDialogComponent,
    PageHeaderComponent,
    ProgressBarComponent,
    CodeIdChipComponent,
    ProjectNavComponent,
    SavedViewsMenuComponent,
    AvatarComponent,
    PlanStatusPillComponent,
  ],
  templateUrl: './test-plans.component.html',
  styleUrl: './test-plans.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestPlansComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(TestPlanService);
  private savedViewService = inject(SavedViewService);
  private destroyRef = inject(DestroyRef);
  private translate = inject(TranslateService);
  private pageSizePref = inject(PageSizePreferenceService);
  private projectRoleService = inject(ProjectRoleService);
  private saveToast = inject(SaveToastService);

  readonly projectId: string;
  readonly plans = signal<TestPlan[]>([]);
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly deleteError = signal(false);
  readonly deleteErrorKey = signal('testPlans.list.deleteError');
  readonly isReadOnly = computed(
    () =>
      this.projectRoleService.isArchived() ||
      (this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin()),
  );
  readonly deleting = signal(false);
  readonly planToDelete = signal<TestPlan | null>(null);
  readonly planToArchive = signal<TestPlan | null>(null);
  readonly archiving = signal(false);
  readonly archiveError = signal<string | null>(null);
  readonly startPlan = signal<TestPlan | null>(null);
  readonly mixedStartPlan = computed(() => {
    const plan = this.startPlan();
    return plan?.executionMode === 'MIXED' ? plan : null;
  });
  readonly realRunPlan = computed(() => {
    const plan = this.startPlan();
    return plan && plan.executionMode !== 'MIXED' ? plan : null;
  });

  readonly searchTerm = signal('');
  readonly assigneeFilter = signal<string | null>(null);
  readonly releaseFilter = signal<string | null>(null);
  readonly statusFilter = signal<TestPlanStatus | null>(null);
  readonly typeFilter = signal<TestPlanType | null>(null);
  readonly tagFilter = signal<string[]>([]);

  /**
   * Alt-Ansichten aus Vor-NEX-1059-Zeiten koennen sowohl `query` als auch
   * `version` gesetzt haben (auf develop schrieb `onSavedViewSaved` beide
   * unabhaengig). Der Version-Chip ist entfallen, `version` bleibt unsichtbar —
   * ohne dieses Signal wuerde er beim Re-Save mit `null` ueberschrieben und die
   * gespeicherte Version waere weg (Datenverlust, Review-Nachfassung C1).
   * Wir spiegeln den Wert bei Lade-Zeit nur dann in die Suche, wenn `query` leer
   * ist; sonst tragen wir ihn hier durch bis zum naechsten Speichern.
   *
   * Lebensdauer: der Puffer gilt nur so lange, wie die geladene Alt-Ansicht die
   * aktive ist. Verlaesst der Nutzer sie durch eine manuelle Aenderung
   * (`clearActiveView`), wird der Puffer geleert — sonst wuerde er in eine neu
   * gespeicherte Ansicht wandern, die der Nutzer nie so gefiltert hat
   * (Review-Nachfassung 2, C1).
   */
  private readonly preservedVersion = signal<string | null>(null);
  readonly sortKey = signal<SortKey>('publicId');
  readonly sortDirection = signal<SortDirection>('desc');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE));
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly legend = ['passed', 'failed', 'blocked', 'open'] as const;

  readonly visiblePlans = computed(() => this.plans().filter((plan) => !plan.archivedAt));

  readonly availableAssignees = computed(() => {
    const map = new Map<string, string>();
    for (const plan of this.visiblePlans()) {
      if (plan.assigneeUserId && !map.has(plan.assigneeUserId)) {
        map.set(plan.assigneeUserId, plan.assigneeName ?? plan.assigneeUserId);
      }
    }
    return Array.from(map, ([id, name]) => ({ id, name }));
  });

  readonly assigneeDropdownOptions = computed<FilterDropdownOption[]>(() =>
    this.availableAssignees().map((a) => ({ value: a.id, label: a.name })),
  );

  readonly tagOptions = computed(() => countTagOptions(this.visiblePlans(), this.tagFilter()));

  readonly assigneeFilterValue = computed(() => this.assigneeFilter() ?? '');

  readonly releaseFilterValue = computed(() => this.releaseFilter() ?? '');
  readonly statusFilterValue = computed(() => this.statusFilter() ?? '');
  readonly typeFilterValue = computed(() => this.typeFilter() ?? '');

  readonly hasFilters = computed(
    () =>
      !!this.searchTerm() ||
      this.assigneeFilter() !== null ||
      this.releaseFilter() !== null ||
      this.statusFilter() !== null ||
      this.typeFilter() !== null ||
      this.tagFilter().length > 0,
  );

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  private readonly langChange = toSignal(this.translate.onLangChange.pipe(startWith(null)), { initialValue: null });

  readonly savedViewsOptions = computed<SavedViewOption[]>(() => {
    this.langChange();
    return this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) }));
  });

  readonly releaseDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.langChange();
    const releases = new Set(this.visiblePlans().flatMap((plan) => (plan.releaseId ? [plan.releaseId] : [])));
    return [...releases]
      .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
      .map((id) => ({ value: id, label: this.releaseLabel(id) }));
  });

  readonly statusDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.langChange();
    return TEST_PLAN_STATUSES.map((status) => ({ value: status, label: this.statusLabel(status) }));
  });

  readonly typeDropdownOptions = computed<FilterDropdownOption[]>(() => {
    this.langChange();
    return TEST_PLAN_TYPES.map((type) => ({ value: type, label: this.typeLabel(type) }));
  });

  readonly stats = computed((): StatItem[] => {
    this.langChange();
    const rows = this.matchingPlans();
    const today = localIsoDate(new Date());
    const count = (match: (plan: TestPlan) => boolean) => rows.filter(match).length;
    return [
      {
        label: this.translate.instant(this.hasFilters() ? 'testPlans.stats.filtered' : 'testPlans.stats.total'),
        value: rows.length,
      },
      {
        label: this.translate.instant('testPlans.stats.active'),
        value: count((p) => p.status === 'AKTIV'),
        color: 'var(--to-brand)',
      },
      {
        label: this.translate.instant('testPlans.stats.overdue'),
        value: count((p) => isOverdue(p, today)),
        color: 'var(--to-red-dot)',
      },
      {
        label: this.translate.instant('testPlans.stats.done'),
        value: count((p) => p.status === 'ABGESCHLOSSEN'),
        color: 'var(--to-green-fg)',
      },
    ];
  });

  private readonly matchingPlans = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const assigneeF = this.assigneeFilter();
    const release = this.releaseFilter();
    const status = this.statusFilter();
    const type = this.typeFilter();
    const tags = this.tagFilter();

    let rows = this.visiblePlans();
    if (release) {
      rows = rows.filter((p) => p.releaseId === release);
    }
    if (term) {
      rows = rows.filter(
        (p) =>
          p.title.toLowerCase().includes(term) ||
          (p.publicId ?? '').toLowerCase().includes(term) ||
          (p.releaseId ?? '').toLowerCase().includes(term) ||
          (p.releaseLabel ?? '').toLowerCase().includes(term),
      );
    }
    if (assigneeF) {
      rows = rows.filter((p) => p.assigneeUserId === assigneeF);
    }
    if (status) {
      rows = rows.filter((p) => p.status === status);
    }
    if (type) {
      rows = rows.filter((p) => testPlanType(p) === type);
    }
    return rows.filter((p) => tagMatch(p.tags, tags));
  });

  readonly filteredPlans = computed(() => {
    const key = this.sortKey();
    const dir = this.sortDirection();
    return [...this.matchingPlans()].sort((a, b) => this.compare(a, b, key, dir));
  });

  readonly total = computed(() => this.filteredPlans().length);
  readonly unfilteredTotal = computed(() => this.visiblePlans().length);
  readonly resultCountKey = computed(() =>
    this.unfilteredTotal() === 1 ? 'testPlans.list.resultCountOne' : 'testPlans.list.resultCountOther',
  );
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));
  readonly rangeStart = computed(() => (this.total() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1));
  readonly rangeEnd = computed(() => Math.min(this.total(), (this.pageIndex() + 1) * this.pageSize()));

  readonly pagedPlans = computed(() => {
    const start = this.pageIndex() * this.pageSize();
    return this.filteredPlans().slice(start, start + this.pageSize());
  });

  constructor() {
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.loadPlans();
    this.loadSavedViews();
  }

  loadPlans(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .list(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (plans) => {
          this.plans.set(plans);
          this.pruneStaleFilters();
          this.loading.set(false);
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  private loadSavedViews(): void {
    this.savedViewService
      .list(this.projectId, 'TEST_PLANS')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (views) => this.savedViews.set(views),
        error: () => this.savedViews.set([]),
      });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestPlanSavedViewFilters(view.filtersJson);
    const assigneeName = filters.assignee
      ? (this.availableAssignees().find((a) => a.id === filters.assignee)?.name ?? filters.assignee)
      : null;
    // Legacy-Ansicht mit ausschliesslich version: sonst waere die Zusammenfassung
    // "Alle", obwohl die Auswahl filtert (NEX-1059-Review Info).
    const effectiveQuery = filters.query ?? filters.version;
    const parts = [
      effectiveQuery,
      filters.release ? this.releaseLabel(filters.release) : null,
      filters.status ? this.statusLabel(filters.status) : null,
      filters.type ? this.typeLabel(filters.type) : null,
      assigneeName,
      filters.tags.join(', '),
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('testPlans.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestPlanSavedViewFilters(view.filtersJson);
    // Drei Faelle einer Alt-Ansicht (NEX-1059-Review C1):
    //   query gesetzt, version gesetzt   -> query gewinnt sichtbar,
    //                                       version bleibt unsichtbar im preservedVersion-Puffer.
    //   query leer,   version gesetzt   -> version in die Suche spiegeln
    //                                       (Migration; die Suche matcht das Release).
    //   sonst                            -> beides leer.
    // Wichtig ist, dass beim Re-Save kein gespeicherter Wert stumm ueberschrieben
    // wird, den der Nutzer nie in einem Bedienelement gesehen hat.
    if (filters.query) {
      this.searchTerm.set(filters.query);
      this.preservedVersion.set(filters.version);
    } else if (filters.version) {
      this.searchTerm.set(filters.version);
      this.preservedVersion.set(null);
    } else {
      this.searchTerm.set('');
      this.preservedVersion.set(null);
    }
    this.assigneeFilter.set(filters.assignee);
    // Ohne Pläne (Laden läuft oder schlug fehl) ist nichts als unbekannt erkennbar; pruneStaleFilters verwirft es nach
    // dem nächsten erfolgreichen Laden.
    const plansMissing = this.loading() || this.loadError();
    this.releaseFilter.set(plansMissing ? filters.release : this.withoutUnknownRelease(filters.release));
    this.statusFilter.set(filters.status);
    this.typeFilter.set(filters.type ?? null);
    this.tagFilter.set(plansMissing ? filters.tags : this.withoutUnknownTags(filters.tags));
    this.pageIndex.set(0);
    this.activeSavedViewName.set(view.name);
  }

  private pruneStaleFilters(): void {
    this.releaseFilter.set(this.withoutUnknownRelease(this.releaseFilter()));
    this.tagFilter.set(this.withoutUnknownTags(this.tagFilter()));
  }

  private withoutUnknownRelease(release: string | null): string | null {
    return this.releaseDropdownOptions().some((o) => o.value === release) ? release : null;
  }

  private withoutUnknownTags(tags: readonly string[]): string[] {
    const knownTags = new Set(this.visiblePlans().flatMap((plan) => plan.tags ?? []));
    return tags.filter((tag) => knownTags.has(tag));
  }

  onSavedViewSaved(name: string): void {
    const type = this.typeFilter();
    const filtersJson = buildTestPlanSavedViewFiltersJson({
      query: this.searchTerm() || null,
      // Alt-Wert unveraendert durchreichen, wenn er dem Nutzer nicht angezeigt
      // wurde. Neue Ansichten haben preservedVersion=null und schreiben null.
      version: this.preservedVersion(),
      assignee: this.assigneeFilter(),
      release: this.releaseFilter(),
      status: this.statusFilter(),
      tags: this.tagFilter(),
      ...(type ? { type } : {}),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'TEST_PLANS', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('testPlans.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      // clearActiveView() statt aktivName direkt zu leeren — sonst ueberlebt
      // preservedVersion die geloeschte Ansicht und wandert beim naechsten
      // Speichern in eine neue Ansicht, die der Nutzer nie so gefiltert hat
      // (Review-Nachfassung, sgraetz).
      this.clearActiveView();
    }
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testPlans.savedViews.deleteError'));
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
    // Der Legacy-Version-Puffer gehoert zur geladenen Ansicht; sobald der Nutzer
    // sie manuell verlaesst, darf er nicht in eine neu gespeicherte Ansicht
    // wandern (Review-Nachfassung 2, C1).
    this.preservedVersion.set(null);
  }

  private onFilterChanged(): void {
    this.pageIndex.set(0);
    this.clearActiveView();
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.onFilterChanged();
  }

  setAssigneeFilter(id: string | null): void {
    this.assigneeFilter.set(id || null);
    this.onFilterChanged();
  }

  setReleaseFilter(release: string | null): void {
    this.releaseFilter.set(release || null);
    this.onFilterChanged();
  }

  setStatusFilter(status: string | null): void {
    this.statusFilter.set(TEST_PLAN_STATUSES.find((known) => known === status) ?? null);
    this.onFilterChanged();
  }

  setTypeFilter(type: string | null): void {
    this.typeFilter.set(TEST_PLAN_TYPES.find((known) => known === type) ?? null);
    this.onFilterChanged();
  }

  setTagFilter(tags: string[]): void {
    this.tagFilter.set(tags);
    this.onFilterChanged();
  }

  resetFilters(): void {
    this.searchTerm.set('');
    this.assigneeFilter.set(null);
    this.releaseFilter.set(null);
    this.statusFilter.set(null);
    this.typeFilter.set(null);
    this.tagFilter.set([]);
    this.onFilterChanged();
  }

  toggleSort(key: SortKey): void {
    if (this.sortKey() === key) {
      this.sortDirection.update((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortKey.set(key);
      this.sortDirection.set('asc');
    }
    this.pageIndex.set(0);
  }

  onPageChange(index: number): void {
    this.pageIndex.set(index);
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
  }

  ariaSort(key: SortKey): 'ascending' | 'descending' | 'none' {
    if (this.sortKey() !== key) return 'none';
    return this.sortDirection() === 'asc' ? 'ascending' : 'descending';
  }

  onCreate(): void {
    this.router.navigate(['/projects', this.projectId, 'test-plans', 'new']);
  }

  onEdit(plan: TestPlan, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/projects', this.projectId, 'test-plans', plan.id, 'edit']);
  }

  onOpenRow(plan: TestPlan): void {
    if (this.projectRoleService.isArchived()) {
      this.onOpenReport(plan);
    } else {
      this.onEdit(plan);
    }
  }

  onRowKeydown(event: Event, plan: TestPlan): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.onOpenRow(plan);
  }

  onOpenReport(plan: TestPlan, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/projects', this.projectId, 'test-plans', plan.id, 'report']);
  }

  canStart(plan: TestPlan): boolean {
    return !this.isReadOnly() && plan.status !== 'ABGESCHLOSSEN' && !!plan.executionMode && plan.testCaseIds.length > 0;
  }

  onAskStart(plan: TestPlan, event: Event): void {
    event.stopPropagation();
    this.startPlan.set(plan);
  }

  onCancelStart(): void {
    this.startPlan.set(null);
  }

  onEditMixedPlan(): void {
    const plan = this.mixedStartPlan();
    this.startPlan.set(null);
    if (plan) this.onEdit(plan);
  }

  onStartRealRun(): void {
    const plan = this.realRunPlan();
    this.startPlan.set(null);
    if (!plan) return;
    this.router.navigate(['/projects', this.projectId, 'test-runs', 'new'], {
      queryParams: realRunQueryParams(plan.id, plan.executionMode === 'AUTOMATED'),
    });
  }

  onDuplicate(plan: TestPlan, event: Event): void {
    event.stopPropagation();
    this.router.navigate(['/projects', this.projectId, 'test-plans', 'new'], { queryParams: { vorlage: plan.id } });
  }

  onAskArchive(plan: TestPlan, event: Event): void {
    event.stopPropagation();
    this.archiveError.set(null);
    this.planToArchive.set(plan);
  }

  onCancelArchive(): void {
    if (this.archiving()) return;
    this.planToArchive.set(null);
  }

  onConfirmArchive(): void {
    const plan = this.planToArchive();
    if (!plan) return;
    this.archiving.set(true);
    this.archiveError.set(null);
    this.service
      .archive(this.projectId, plan.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.removePlan(plan.id);
          this.archiving.set(false);
          this.planToArchive.set(null);
          this.saveToast.show(this.translate.instant('testPlans.list.archiveDialog.toast'));
        },
        error: (err: unknown) => {
          this.archiving.set(false);
          this.archiveError.set(archiveErrorKey(err));
        },
      });
  }

  onAskDelete(plan: TestPlan, event: Event): void {
    event.stopPropagation();
    this.deleteError.set(false);
    this.planToDelete.set(plan);
  }

  onCancelDelete(): void {
    if (this.deleting()) return;
    this.planToDelete.set(null);
  }

  onConfirmDelete(): void {
    const plan = this.planToDelete();
    if (!plan) return;
    this.deleting.set(true);
    this.deleteError.set(false);
    this.service
      .delete(this.projectId, plan.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.removePlan(plan.id);
          this.deleting.set(false);
          this.planToDelete.set(null);
        },
        error: (err: unknown) => {
          this.deleting.set(false);
          this.deleteErrorKey.set(writeErrorKey(err, 'testPlans.list.deleteError'));
          this.deleteError.set(true);
        },
      });
  }

  private removePlan(id: string): void {
    this.plans.update((list) => list.filter((p) => p.id !== id));
    const release = this.releaseFilter();
    if (release && !this.releaseDropdownOptions().some((o) => o.value === release)) {
      this.setReleaseFilter(null);
    }
    if (this.pageIndex() >= this.pageCount()) {
      this.pageIndex.set(Math.max(0, this.pageCount() - 1));
    }
  }

  progressPercent(plan: TestPlan): number {
    const total = plan.progress.total || 1;
    return Math.round(((plan.progress.passed + plan.progress.failed + plan.progress.blocked) / total) * 100);
  }

  planType(plan: TestPlan): TestPlanType {
    return testPlanType(plan);
  }

  runSummaryOf(plan: TestPlan): RunSummary {
    return runSummary(plan);
  }

  isPlanOverdue(plan: TestPlan): boolean {
    return isOverdue(plan, localIsoDate(new Date()));
  }

  trackById(_: number, plan: TestPlan): string {
    return plan.id;
  }

  private releaseLabel(id: string): string {
    return this.translate.instant('testPlans.list.filter.releaseOption', { id });
  }

  private statusLabel(status: TestPlanStatus): string {
    return this.translate.instant(`testPlans.status.${status}`);
  }

  private typeLabel(type: TestPlanType): string {
    return this.translate.instant(`testPlans.type.${type}`);
  }

  private compare(a: TestPlan, b: TestPlan, key: SortKey, dir: SortDirection): number {
    return this.compareBy(a, b, key, dir) || this.compareIdDescending(a, b);
  }

  private compareIdDescending(a: TestPlan, b: TestPlan): number {
    const av = a.publicId ?? '';
    const bv = b.publicId ?? '';
    if (av === bv) return 0;
    return av < bv ? 1 : -1;
  }

  private compareBy(a: TestPlan, b: TestPlan, key: SortKey, dir: SortDirection): number {
    const av = this.sortValue(a, key);
    const bv = this.sortValue(b, key);
    if (av === bv) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  }

  private sortValue(plan: TestPlan, key: SortKey): string | number | null {
    switch (key) {
      case 'publicId':
        return plan.publicId ?? '';
      case 'title':
        return plan.title.toLowerCase();
      case 'release':
        return plan.releaseId?.toLowerCase() ?? null;
      case 'status':
        return STATUS_RANK[plan.status];
      case 'progress':
        return this.progressPercent(plan);
      case 'assignee':
        return plan.assigneeName?.toLowerCase() ?? null;
      case 'dueDate':
        return plan.dueDate ?? null;
    }
  }
}
